import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { getCustomerServicePhone, getCustomerServiceTel, HUD_COUNSELING, WA_HOMEOWNERSHIP_HOTLINE } from '@ld.consumer-portal/constants';
import { PaymentAssistanceTopic, SecureMessageCategory } from '@ld.consumer-portal/models';
import { BrowserService, LoanSelectionService, PlatformService } from '@ld.consumer-portal/utils';
import { ContactCardComponent } from '../contact-card/contact-card.component';
// eslint-disable-next-line @nx/enforce-module-boundaries -- compose message is a shared feature component used in @defer block
import { ComposeMessageComponent } from '@ld.consumer-portal/documents';

interface PaymentOption {
  key: string;
  titleKey: string;
  detailsKey: string;
  details2Key?: string;
}

interface DownloadDocument {
  textKey: string;
  url: string;
}

@Component({
  selector: 'ld-financial-hardship-page',
  imports: [ComposeMessageComponent, ContactCardComponent, MatExpansionModule, MatIconModule, TranslocoPipe],
  templateUrl: './financial-hardship-page.component.html',
  styleUrl: './financial-hardship-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinancialHardshipPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly loanSelectionService = inject(LoanSelectionService);
  private readonly platformService = inject(PlatformService);
  private readonly browserService = inject(BrowserService);

  protected readonly isNativeMobile = this.platformService.isNativeMobile;

  private readonly accountId = this.route.snapshot.paramMap.get('accountId');

  protected readonly faqsLink = computed(() => {
    return this.accountId ? `/help/${this.accountId}/faqs` : '/dashboard';
  });

  protected readonly financialHardshipLink = computed(() => {
    return this.accountId ? `/help/${this.accountId}/financial-hardship` : '/dashboard';
  });

  protected readonly propertyTaxesLink = computed(() => {
    return this.accountId ? `/help/${this.accountId}/property-taxes` : '/dashboard';
  });

  protected readonly homeownersInsuranceLink = computed(() => {
    return this.accountId ? `/help/${this.accountId}/homeowners-insurance` : '/dashboard';
  });

  protected readonly insuranceClaimsLink = computed(() => {
    return this.accountId ? `/help/${this.accountId}/insurance-claims` : '/dashboard';
  });

  protected readonly showComposeMessage = signal(false);
  protected readonly composePrefillCategory = SecureMessageCategory.PaymentAssistance;
  protected readonly composePrefillTopic = signal<string>(PaymentAssistanceTopic.PaymentAssistanceDocuments);

  protected readonly expandedPanels = signal<Set<string>>(new Set());

  protected readonly customerServiceTel = computed(() => getCustomerServiceTel(this.loanSelectionService.customerServiceLoanCategory()));
  protected readonly customerServicePhone = computed(() => getCustomerServicePhone(this.loanSelectionService.customerServiceLoanCategory()));
  protected readonly hudCounselingTel = `tel:${HUD_COUNSELING.phone}`;
  protected readonly waHotlineTel = `tel:${WA_HOMEOWNERSHIP_HOTLINE.phone}`;

  protected readonly paymentOptions: PaymentOption[] = [
    {
      key: 'repaymentPlan',
      titleKey: 'paymentAmountRepaymentPlanPayment',
      detailsKey: 'financialHardshipOptionRepaymentPlanDetails',
    },
    {
      key: 'forbearancePlan',
      titleKey: 'financialHardshipOptionForbearancePlanTitle',
      detailsKey: 'financialHardshipOptionForbearancePlanDetails',
    },
    {
      key: 'loanModification',
      titleKey: 'financialHardshipOptionLoanModificationTitle',
      detailsKey: 'financialHardshipOptionLoanModificationDetails',
    },
    {
      key: 'partialClaim',
      titleKey: 'financialHardshipOptionPartialClaimTitle',
      detailsKey: 'financialHardshipOptionPartialClaimDetails1',
      details2Key: 'financialHardshipOptionPartialClaimDetails2',
    },
    {
      key: 'shortSale',
      titleKey: 'financialHardshipOptionShortSaleTitle',
      detailsKey: 'financialHardshipOptionShortSaleDetails',
    },
    {
      key: 'deedInLieu',
      titleKey: 'financialHardshipOptionDeedInLieuTitle',
      detailsKey: 'financialHardshipOptionDeedInLieuDetails',
    },
  ];

  protected readonly infoDocuments: DownloadDocument[] = [
    {
      textKey: 'financialHardshipDocInfoAvoidingForeclosure',
      url: '/assets/pdf/information-avoiding-foreclosure.pdf',
    },
    {
      textKey: 'financialHardshipDocFaqsBewareScams',
      url: '/assets/pdf/f-a-qs-and-beware-scams-information.pdf',
    },
  ];

  protected readonly formDocuments: DownloadDocument[] = [
    {
      textKey: 'financialHardshipFormMortgageApplication',
      url: '/assets/pdf/Mortgage Assistance Application.pdf',
    },
    {
      textKey: 'financialHardshipFormIrs4506c',
      url: '/assets/pdf/IRS FORM 4506C.pdf',
    },
    {
      textKey: 'financialHardshipFormMonthlyExpense',
      url: '/assets/pdf/Monthly Financial Expense.pdf',
    },
  ];

  togglePanel(key: string): void {
    this.expandedPanels.update((current) => {
      const newSet = new Set(current);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }

  isPanelExpanded(key: string): boolean {
    return this.expandedPanels().has(key);
  }

  openSecureMessage(topic?: string): void {
    this.composePrefillTopic.set(topic || PaymentAssistanceTopic.Question);
    this.showComposeMessage.set(true);
  }

  openUploadDialog(): void {
    this.composePrefillTopic.set(PaymentAssistanceTopic.PaymentAssistanceDocuments);
    this.showComposeMessage.set(true);
  }

  onCloseComposeMessage(): void {
    this.showComposeMessage.set(false);
  }

  openDocument(event: Event, url: string): void {
    event.preventDefault();
    this.browserService.openLocalAsset(url);
  }
}
